/**
 * NodeConfigPanel - Right sidebar for configuring selected nodes
 * Shows different configuration options based on node type
 *
 * Node Types:
 * - orchestrator: Editable systemMessage (query-understanding, direct-response, synthesis)
 * - agent: Editable systemMessage (similarity, reference, structure, image, table)
 * - condition: Read-only (shows branches info)
 * - system: Read-only (init, retrieval, reflection)
 * - start/end: Minimal config
 */

import { memo, useCallback } from 'react';
import { useWorkflowSelection, useWorkflowStore } from '../../../../stores/workflow';
import type {
  AgentNodeData,
  ToolNodeData,
  ConditionNodeData,
  EndNodeData,
  AgentType,
  OrchestratorNodeData,
  OrchestratorNodeType,
  SystemNodeData
} from '../../../../stores/workflow/types';
import { hasEditablePrompt } from '../../../../stores/workflow/types';

export const NodeConfigPanel = memo(() => {
  const { selectedNodeId, clearSelection } = useWorkflowSelection();
  const updateNodeData = useWorkflowStore((s) => s.updateNodeData);
  const removeNode = useWorkflowStore((s) => s.removeNode);

  // Subscribe to nodes so the component re-renders when node data changes (e.g., typing in textarea)
  const selectedNode = useWorkflowStore((s) =>
    s.selectedNodeId ? s.nodes.find((n) => n.id === s.selectedNodeId) : undefined
  );

  const handleClose = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  const handleDelete = useCallback(() => {
    if (selectedNodeId) {
      removeNode(selectedNodeId);
    }
  }, [selectedNodeId, removeNode]);

  if (!selectedNode) return null;

  // Determine if node is deletable (not start, end, or fixed system nodes)
  const isDeletable = !['start', 'end'].includes(selectedNode.data.type);

  return (
    <div className='h-full w-80 flex-shrink-0 overflow-y-auto border-l border-gray-200 bg-white dark:border-border-dark dark:bg-dark-background-secondary'>
      {/* Header */}
      <div className='sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 dark:border-border-dark dark:bg-dark-background-secondary'>
        <div>
          <h3 className='text-sm font-semibold text-gray-900 dark:text-dark-text'>Configure Node</h3>
          <p className='text-xs text-gray-500 dark:text-dark-text-secondary'>{selectedNode.data.label}</p>
        </div>
        <button
          onClick={handleClose}
          className='rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-background-hover dark:hover:text-dark-text-secondary'
        >
          <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
          </svg>
        </button>
      </div>

      {/* Content - Based on node type */}
      <div className='p-4'>
        {/* Common: Label (editable for most nodes) */}
        <div className='mb-4'>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>Label</label>
          <input
            type='text'
            value={selectedNode.data.label}
            onChange={(e) => updateNodeData(selectedNode.id, { label: e.target.value })}
            className='w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
          />
        </div>

        {/* Type-specific configuration */}
        {selectedNode.data.type === 'orchestrator' && (
          <OrchestratorConfigForm nodeId={selectedNode.id} data={selectedNode.data as OrchestratorNodeData} updateNodeData={updateNodeData} />
        )}

        {selectedNode.data.type === 'agent' && (
          <AgentConfigForm nodeId={selectedNode.id} data={selectedNode.data as AgentNodeData} updateNodeData={updateNodeData} />
        )}

        {selectedNode.data.type === 'tool' && (
          <ToolConfigForm nodeId={selectedNode.id} data={selectedNode.data as ToolNodeData} updateNodeData={updateNodeData} />
        )}

        {selectedNode.data.type === 'condition' && <ConditionInfoPanel data={selectedNode.data as ConditionNodeData} />}

        {selectedNode.data.type === 'system' && <SystemInfoPanel data={selectedNode.data as SystemNodeData} />}

        {selectedNode.data.type === 'end' && (
          <EndConfigForm nodeId={selectedNode.id} data={selectedNode.data as EndNodeData} updateNodeData={updateNodeData} />
        )}

        {/* Prompt indicator for editable nodes */}
        {hasEditablePrompt(selectedNode.data) && (
          <div className='mt-4 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20'>
            <p className='text-xs text-blue-700 dark:text-blue-300'>
              The system message defines how this node processes input. Edit it to customize behavior.
            </p>
          </div>
        )}

        {/* Delete Button */}
        {isDeletable && (
          <div className='mt-6 border-t border-gray-200 pt-4 dark:border-border-dark'>
            <button
              onClick={handleDelete}
              className='w-full rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-rose-400/50'
            >
              Delete Node
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

NodeConfigPanel.displayName = 'NodeConfigPanel';

// ===== Orchestrator Config Form =====
interface OrchestratorConfigFormProps {
  nodeId: string;
  data: OrchestratorNodeData;
  updateNodeData: (nodeId: string, data: Partial<OrchestratorNodeData>) => void;
}

const OrchestratorConfigForm = memo(({ nodeId, data, updateNodeData }: OrchestratorConfigFormProps) => {
  const getPlaceholderHint = (nodeType: OrchestratorNodeType): string => {
    switch (nodeType) {
      case 'query_understanding':
        return 'Mô tả cách phân tích ý định người dùng và quyết định có cần tìm kiếm KG hay không...';
      case 'direct_response':
        return 'Mô tả cách trả lời trực tiếp khi không cần tìm kiếm trong KG...';
      case 'synthesis':
        return 'Mô tả cách tổng hợp kết quả từ các agent thành câu trả lời cuối cùng...';
      default:
        return 'Mô tả hành vi của bước này trong workflow...';
    }
  };

  const getNodeTypeLabel = (nodeType: OrchestratorNodeType): string => {
    switch (nodeType) {
      case 'query_understanding':
        return 'Query Understanding';
      case 'direct_response':
        return 'Direct Response';
      case 'synthesis':
        return 'Synthesis';
      default:
        return nodeType;
    }
  };

  return (
    <div className='space-y-4'>
      {/* Node Type (read-only) */}
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>Step Type</label>
        <div className='rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 dark:bg-dark-background-tertiary dark:text-dark-text-secondary'>
          {getNodeTypeLabel(data.nodeType)}
        </div>
      </div>

      {/* System Message */}
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>System Message (Prompt)</label>
        <textarea
          value={data.systemMessage || ''}
          onChange={(e) => updateNodeData(nodeId, { systemMessage: e.target.value })}
          rows={8}
          className='w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text min-h-[200px] max-h-[60vh] resize-y overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100'
          placeholder={getPlaceholderHint(data.nodeType)}
        />
      </div>
    </div>
  );
});

OrchestratorConfigForm.displayName = 'OrchestratorConfigForm';

// ===== Agent Config Form =====
interface AgentConfigFormProps {
  nodeId: string;
  data: AgentNodeData;
  updateNodeData: (nodeId: string, data: Partial<AgentNodeData>) => void;
}

const AgentConfigForm = memo(({ nodeId, data, updateNodeData }: AgentConfigFormProps) => {
  const agentTypes: { value: AgentType; label: string }[] = [
    { value: 'similarity', label: 'Similarity Agent' },
    { value: 'reference', label: 'Reference Agent' },
    { value: 'structure', label: 'Structure Agent' },
    { value: 'image', label: 'Image Agent' },
    { value: 'table', label: 'Table Agent' },
    { value: 'direct_qa', label: 'Direct QA Agent' },
    { value: 'custom', label: 'Custom Agent' }
  ];

  const getPlaceholderHint = (agentType: AgentType): string => {
    switch (agentType) {
      case 'similarity':
        return 'Mô tả cách tìm kiếm nội dung tương tự dựa trên embedding và từ khóa...';
      case 'reference':
        return 'Mô tả cách phân tích tham chiếu và mối quan hệ khái niệm...';
      case 'structure':
        return 'Mô tả cách phân tích cấu trúc tài liệu và mối quan hệ phân cấp...';
      case 'image':
        return 'Mô tả cách xử lý và phân tích hình ảnh trong tài liệu...';
      case 'table':
        return 'Mô tả cách phân tích và truy vấn dữ liệu bảng biểu...';
      case 'direct_qa':
        return 'System prompt cho Direct QA agent - hướng dẫn cách trả lời dựa trên tài liệu...';
      default:
        return 'Mô tả hành vi và mục tiêu của agent này...';
    }
  };

  return (
    <div className='space-y-4'>
      {/* Agent Type */}
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>Agent Type</label>
        <select
          value={data.agentType}
          onChange={(e) => updateNodeData(nodeId, { agentType: e.target.value as AgentType })}
          className='w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
        >
          {agentTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* System Message */}
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>System Message (Prompt)</label>
        <textarea
          value={data.systemMessage || ''}
          onChange={(e) => updateNodeData(nodeId, { systemMessage: e.target.value })}
          rows={8}
          className='w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text min-h-[200px] max-h-[60vh] resize-y overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100'
          placeholder={getPlaceholderHint(data.agentType)}
        />
      </div>
    </div>
  );
});

AgentConfigForm.displayName = 'AgentConfigForm';

// ===== Tool Config Form =====
interface ToolConfigFormProps {
  nodeId: string;
  data: ToolNodeData;
  updateNodeData: (nodeId: string, data: Partial<ToolNodeData>) => void;
}

const ToolConfigForm = memo(({ nodeId, data, updateNodeData }: ToolConfigFormProps) => {
  return (
    <div className='space-y-4'>
      {/* Tool Type */}
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>Tool Type</label>
        <select
          value={data.toolType || ''}
          onChange={(e) => updateNodeData(nodeId, { toolType: e.target.value })}
          className='w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
        >
          <option value=''>Select a tool type...</option>
          <option value='web_search'>Web Search</option>
          <option value='calculator'>Calculator</option>
          <option value='code_interpreter'>Code Interpreter</option>
          <option value='api_call'>API Call</option>
          <option value='database_query'>Database Query</option>
          <option value='custom'>Custom Tool</option>
        </select>
      </div>

      {/* Tool ID */}
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>Tool ID</label>
        <input
          type='text'
          value={data.toolId || ''}
          onChange={(e) => updateNodeData(nodeId, { toolId: e.target.value })}
          className='w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
          placeholder='Enter tool ID...'
        />
      </div>
    </div>
  );
});

ToolConfigForm.displayName = 'ToolConfigForm';

// ===== Condition Info Panel (Read-Only) =====
interface ConditionInfoPanelProps {
  data: ConditionNodeData;
}

const ConditionInfoPanel = memo(({ data }: ConditionInfoPanelProps) => {
  return (
    <div className='space-y-4'>
      {/* Read-only notice */}
      <div className='rounded-lg bg-amber-50 p-3 dark:bg-amber-900/20'>
        <div className='flex items-center gap-2'>
          <svg className='h-4 w-4 text-amber-600 dark:text-amber-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 15v2m0 0v2m0-2h2m-2 0H9m3-10V5a2 2 0 10-4 0v4H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2h-4V5a2 2 0 00-4 0'
            />
          </svg>
          <span className='text-sm font-medium text-amber-700 dark:text-amber-300'>Read-Only Node</span>
        </div>
        <p className='mt-1 text-xs text-amber-600 dark:text-amber-400'>This condition is determined by the LLM at runtime and cannot be edited.</p>
      </div>

      {/* Description */}
      {data.description && (
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>Description</label>
          <p className='text-sm text-gray-600 dark:text-dark-text-secondary'>{data.description}</p>
        </div>
      )}

      {/* Branches */}
      {data.branches && (
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>Branches</label>
          <div className='space-y-2'>
            <div className='flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 dark:bg-green-900/20'>
              <span className='text-xs font-medium text-green-700 dark:text-green-300'>True →</span>
              <span className='font-mono text-xs text-green-600 dark:text-green-400'>{data.branches.true}</span>
            </div>
            <div className='flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 dark:bg-red-900/20'>
              <span className='text-xs font-medium text-red-700 dark:text-red-300'>False →</span>
              <span className='font-mono text-xs text-red-600 dark:text-red-400'>{data.branches.false}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ConditionInfoPanel.displayName = 'ConditionInfoPanel';

// ===== System Info Panel (Read-Only) =====
interface SystemInfoPanelProps {
  data: SystemNodeData;
}

const SystemInfoPanel = memo(({ data }: SystemInfoPanelProps) => {
  const getNodeTypeDescription = (nodeType: string): string => {
    switch (nodeType) {
      case 'init':
        return 'Initializes the workflow, collects conversation history (max 18k tokens), and summarizes if needed.';
      case 'retrieval':
        return 'Performs hybrid search (α=0.75) in Weaviate to find relevant documents.';
      case 'reflection':
        return 'Evaluates the response quality (currently disabled, uses default scores).';
      default:
        return 'System infrastructure node.';
    }
  };

  return (
    <div className='space-y-4'>
      {/* System node notice */}
      <div className='rounded-lg bg-gray-100 p-3 dark:bg-dark-background-tertiary'>
        <div className='flex items-center gap-2'>
          <svg className='h-4 w-4 text-gray-600 dark:text-dark-text-secondary' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
            />
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
          </svg>
          <span className='text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>System Node</span>
        </div>
        <p className='mt-1 text-xs text-gray-600 dark:text-dark-text-secondary'>This is a fixed infrastructure node and cannot be customized.</p>
      </div>

      {/* Node Type */}
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>Node Type</label>
        <div className='rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 dark:bg-dark-background-tertiary dark:text-dark-text-secondary'>{data.nodeType}</div>
      </div>

      {/* Description */}
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>Purpose</label>
        <p className='text-sm text-gray-600 dark:text-dark-text-secondary'>{data.description || getNodeTypeDescription(data.nodeType)}</p>
      </div>
    </div>
  );
});

SystemInfoPanel.displayName = 'SystemInfoPanel';

// ===== End Config Form =====
interface EndConfigFormProps {
  nodeId: string;
  data: EndNodeData;
  updateNodeData: (nodeId: string, data: Partial<EndNodeData>) => void;
}

const EndConfigForm = memo(({ nodeId, data, updateNodeData }: EndConfigFormProps) => {
  return (
    <div className='space-y-4'>
      {/* Output Format */}
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>Output Format</label>
        <select
          value={data.outputFormat || 'text'}
          onChange={(e) => updateNodeData(nodeId, { outputFormat: e.target.value as 'text' | 'json' | 'markdown' })}
          className='w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
        >
          <option value='text'>Plain Text</option>
          <option value='json'>JSON</option>
          <option value='markdown'>Markdown</option>
        </select>
      </div>
    </div>
  );
});

EndConfigForm.displayName = 'EndConfigForm';
